import ModalComponent from "@general-ui/modal/modal";
import { useMemo, useState } from "react";
import { StaticVideo, VideoType } from "types/working-model";
import Icon, { COLORS, ICONS } from "@general-ui/icon";
import classNames from "classnames";
import { OptionalComponent } from "utils/optional-component";
import ThumbnailEditor from "./new-static-video-thumbail-editor";
import Switch from "@general-ui/switch/switch";
import { GetDefaultNewStaticVideo } from "store/utils/create-event";
import TextInput from "@general-ui/text-input/text";
import CustomHTML from "@general-ui/edit-html/edit-html";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import { showAlert } from "@general-ui/alert/alert-service";
import { UploadedVideo } from "connection/videos";
import NewStaticVideoSelectorList from "./new-static-video-selector-list";

interface NewStaticVideoSelectorProps {
	onSelect: (videos: StaticVideo[] | null) => void;
	selectedVideos?: StaticVideo[] | null;
	handleCancel: () => void;
	multiselect?: boolean;
	videoTypes?: VideoType[];
	forPageModule?: boolean;
}

export default function NewStaticVideoSelector({
	onSelect,
	selectedVideos,
	handleCancel,
	multiselect = false,
	videoTypes = [VideoType.uploaded, VideoType.url, VideoType.embed, VideoType.recorded],
	forPageModule = false,
}: NewStaticVideoSelectorProps): JSX.Element {
	const { user, token } = useTypedSelector(state => state.AuthReducer);

	// Uploaded includes recorded videos in this instance
	const [addVideoType, setAddVideoType] = useState<VideoType>(
		Array.isArray(selectedVideos) 
			? selectedVideos[0]?.type ?? VideoType.uploaded
			: (selectedVideos as StaticVideo | null)?.type ?? VideoType.uploaded
	);
	
	const [workingVideos, setWorkingVideos] = useState<StaticVideo[]>(
		Array.isArray(selectedVideos) 
			? selectedVideos 
			: (selectedVideos ? [selectedVideos as StaticVideo] : [])
	);

	const [selectingType, setSelectingType] = useState(false);

	function setSelectedType(type: VideoType) {
		return () => {
			if (isSelectVideoView(type)) {
				setWorkingVideos([]);
			} else {
				setWorkingVideos([{
					...GetDefaultNewStaticVideo(),
					type
				}]);
			}
			setAddVideoType(type);
			setSelectingType(false);
		};
	}

	const selectedWorkingType = useMemo(() => {
		if (!addVideoType) return null;
		switch (addVideoType) {
			case 'embed':
				return <EmbedIframe />;
			case 'recorded':
			case 'uploaded':
				return <AddVideo />;
			case 'url':
				return <LinkVideo />;
			default:
				return (<></>);
		}
	}, [addVideoType]);

	// Only for url/embed videos that have a single selection
	function handleImageChange(images: { original: string; edited: string }): void {
		if(workingVideos.length === 0) return;
		const updatedVideos = [...workingVideos];
		updatedVideos[0] = {
			...updatedVideos[0],
			original_image: images.original,
			thumbnail_url: images.edited,
		};
		setWorkingVideos(updatedVideos);
	}

	// Only for url/embed videos that have a single selection
	function handleCustomThumbnailToggle(): void {
		if (workingVideos.length === 0) return;
		const updatedVideos = [...workingVideos];
		updatedVideos[0] = {
			...updatedVideos[0],
			use_custom_thumbnail: !updatedVideos[0].use_custom_thumbnail,
		};
		setWorkingVideos(updatedVideos);
	}

	const handleSelectVideo = (video: StaticVideo) => {
		if (multiselect) {
			const videoIndex = workingVideos.findIndex(v => v.uuid === video.uuid);
			if (videoIndex === -1) {
				setWorkingVideos([...workingVideos, video]);
			} else {
				setWorkingVideos(workingVideos.filter(v => v.uuid !== video.uuid));
			}
		} else {
			if (workingVideos[0]?.uuid === video.uuid) {
				setWorkingVideos([]);
			} else {
				setWorkingVideos([video]);
			}
		}
	};

	function handleVideoEmbed(code: string) {
		setWorkingVideos(videos => {
			if (videos.length === 0) return [];
			const updatedVideos = [...videos];
			updatedVideos[0] = {
				...updatedVideos[0],
				source: code
			};
			return updatedVideos;
		});
	}

	function handleVideoUrl({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setWorkingVideos(videos => {
			if (videos.length === 0) return [];
			const updatedVideos = [...videos];
			updatedVideos[0] = {
				...updatedVideos[0],
				source: value
			};
			return updatedVideos;
		});
	}
	
	async function handleDone() {
		if(!user || !token) return;
		
		const processedVideos = await Promise.all(workingVideos.map(async (video) => {
			if (video && !video?.uuid) {
				try {
					return await UploadedVideo(token, video, user.active_channel);
				} catch (e) {
					showAlert({
						message: "Error",
						description: "There was an error saving your video. Please try again.",
						duration: 5000
					});
					return video;
				}
			}
			return video;
		}));
		
		onSelect(processedVideos.length > 0 ? processedVideos : null);
	}

	const renderVideoSelect = useMemo(() => {
		switch (addVideoType) {
			case 'uploaded':
			case 'recorded': {
				return (
					<NewStaticVideoSelectorList
						selectedVideos={workingVideos}
						onSelect={handleSelectVideo}
						multiselect={multiselect}
						videoTypes={videoTypes}
						forPageModule={forPageModule}
					/>
				);
			}
			case 'url': {
				if (workingVideos.length === 0) return null;
				return (
					<TextInput
						placeholder="Enter video URL"
						label={"Add video URL"}
						onChange={handleVideoUrl}
						style={{ marginTop: 16 }}
						defaultValue={workingVideos[0].source ?? ''}
					/>
				);
			}
			case 'embed': {
				if (workingVideos.length === 0) return null;
				return (
					<div className="video-embed-code">
						<label>Embed Code</label>
						<CustomHTML
							onChange={handleVideoEmbed}
							defaultValue={workingVideos[0].source ?? ''}
						/>
					</div>
				);
			}
		}
	}, [addVideoType, workingVideos, multiselect]);

	return (
		<ModalComponent
			open={true}
			onRequestClose={() => { } }
			cancellable={true}
			closeable={false}
			title={multiselect ? 'Add Videos' : 'Add Video'}
			footer={
					<div className="edit-video-footer">
						{(isSelectVideoView(addVideoType)) ? <div /> : (
							<div
								className="field-group switch"
								style={{ marginBottom: 16 }}
							>
								<Switch
									value={'use-thumb'}
									on={!!workingVideos[0]?.use_custom_thumbnail}
									onClick={handleCustomThumbnailToggle}
								/>
								<label>Add custom thumbnail</label>
							</div>
						)}
						<div className="editing-buttons">
							<button
								onClick={handleCancel}
								style={{ marginRight: 16 }}
							>
								Cancel
							</button>
							<button
							onClick={handleDone}
							className="lemonade"
							>
								Done
							</button>
						</div>
					</div>
			}
		>
				<div className="add-video-modal">
					<div className="video-type-select">
						<div className={classNames("video-type-selected", { open: selectingType })} onClick={() => setSelectingType(!selectingType)}>
							{selectedWorkingType}
							<span className="arrow"><Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} /></span>
						</div>
						<div className={classNames("video-type-selector", { open: selectingType })}>
							<div onClick={setSelectedType(VideoType.uploaded)} className="select-option">
								<AddVideo />
							</div>
							<div onClick={setSelectedType(VideoType.url)} className="select-option">
								<LinkVideo />
							</div>
							<div onClick={setSelectedType(VideoType.embed)} className="select-option">
								<EmbedIframe />
							</div>
						</div>
					</div>
					{renderVideoSelect}
				</div>

			<OptionalComponent display={[VideoType.url, VideoType.embed].includes(addVideoType) && workingVideos[0]?.use_custom_thumbnail}>
				<ThumbnailEditor
					workingVideo={workingVideos[0]}
					onImageChange={handleImageChange}
				/>
			</OptionalComponent>
		</ModalComponent>
	);
}

function AddVideo() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.ADD} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Add Video</label>
				<span>Add video from content gallery or upload .mp4 files up to 5 GB</span>
			</div>
		</div>
	);
}

function LinkVideo() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.PAPERCLIP} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Link video</label>
				<span>Use YouTube, Vimeo, Wistia, Mixcloud, Dailymotion, or Twitch link</span>
			</div>
		</div>
	);
}

function EmbedIframe() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CODE} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Embedded Iframe</label>
				<span>Copy & paste the code of embed HTML code</span>
			</div>
		</div>
	);
}

function isSelectVideoView(type: VideoType): boolean {
	return type === 'uploaded' || type === 'recorded';
}